import { useRef } from 'react';

import { FieldValues, useController } from 'react-hook-form';
import type { GroupBase } from 'react-select-5';

import { FormFieldWrapper } from 'components/shared/BaseFormComponents';

import { useOptionToString } from '../../hooks/useOptionToString';
import { Select as FeatureRichSelect } from '../../index';
import { ReactSelectRef } from '../../index.types';
import { HookFormSelectProps } from './types';

export function Select<
  Values extends FieldValues,
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: HookFormSelectProps<Values, Option, IsMulti, Group>) {
  const { control, helpText, optionToString, ...rest } = props;

  const selectRef: ReactSelectRef<Option, IsMulti, Group> = useRef(null);

  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({ name: props.name, control });

  const ots = useOptionToString(
    {
      value: {
        value,
        setValue: onChange,
      },
      selectRef,
    },
    {
      enabled: optionToString,
      isMulti: props.isMulti,
      getOptionValue: props.getOptionValue,
      options: props.options,
      ...(props.async && { async: props.async, loadOptions: props.loadOptions }),
    },
  );

  return (
    <FormFieldWrapper errorMessage={error?.message} helpText={helpText}>
      <FeatureRichSelect<Option, IsMulti, Group>
        value={ots.enabled ? ots.controls?.value : value}
        {...rest}
        async={rest.async as true}
        loadOptions={rest.async ? (ots.enabled ? ots.controls?.loadOptions : props.loadOptions) : undefined}
        onBlur={(e) => {
          onBlur();
          props.onBlur?.(e);
        }}
        onChange={(v, a) => {
          props.onChange?.(v, a);
          if (ots.enabled) {
            ots.controls?.onChange(v);
          } else {
            onChange(v);
          }
        }}
      />
    </FormFieldWrapper>
  );
}
